import { useEffect, useRef, useState } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { MenuRow } from './MenuRow';
import { MenuSurface } from './MenuSurface';
import styles from './ShareMenu.module.css';
import type { HeaderTheme, ShareScope } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ShareMenuProps {
  scope: ShareScope;
  /** Called with the audience picked from the visibility row's select. */
  onScopeChange?: (scope: ShareScope) => void;
  /**
   * The share link, shown as the second row's label. Omit it and that row falls back to reading
   * "Copy link" — there is no point printing an empty box where a URL should be.
   */
  url?: string;
  onCopyLink?: () => void;
  /** Swaps the link row's label for a beat after a successful copy. */
  copied?: boolean;
  /** Stories and visual tests only: opens the visibility select without a real click. */
  defaultScopeMenuOpen?: boolean;
  theme?: HeaderTheme;
  className?: string;
}

/** 340px wide, anchored right edge to the share slot's right edge. */
export const SHARE_MENU_WIDTH = 340;

/**
 * The visibility select. Narrow, because its two rows are two words — a 340px list under a 340px
 * menu would read as a second share menu rather than as one row's answer.
 */
const SCOPE_MENU_WIDTH = 180;

/**
 * Must match `--hv15-dur-menu-out`, and matches `HeaderV15`'s own exit for the same reason: the
 * select stays mounted this long after closing so its exit transition has something to run on.
 */
const SCOPE_MENU_EXIT_MS = 120;

/**
 * Who the deck is visible to, phrased as an audience rather than as a link permission.
 *
 * `link` is the stored value's name — the deck is reachable by anyone holding its URL — but the row
 * answers the question the person opening this menu is actually asking, which is who can see it.
 */
const SCOPE_LABEL: Record<ShareScope, string> = {
  private: 'Only me',
  link: 'Everyone',
};

/** The audience's own mark, used by both the row that reports it and the row that picks it. */
const SCOPE_ICON: Record<ShareScope, string> = {
  // `lock` would be the obvious private glyph, but `lock.svg` is the ONE file in the registry with
  // no width/height attributes, so DeckIcon's sizing regex misses it and it renders at zero.
  // `eye-off` says the same thing and actually draws.
  private: 'eye-off',
  link: 'global',
};

const SCOPE_ORDER: ShareScope[] = ['private', 'link'];

/**
 * Two rows: who the deck is visible to, and the link.
 *
 * The scope used to be a Private/Public segmented control sitting above a row that then restated it
 * ("Anyone with the link", with the link's permission as a tail). That is the same fact told twice,
 * and the segment took a menu's full width to say what one word says. It is gone: the row names the
 * setting, its tail names the current value, and pressing it opens a two-item select — one control,
 * one fact, and the value on screen the whole time rather than inferred from which half of a
 * segment is lit.
 *
 * The select is a real list rather than a press that swaps the value, because a caret promises a
 * list. It also means both audiences are legible before you commit to either, which is the whole
 * reason the segment named both sides in the first place — the naming survives, the second control
 * does not.
 *
 * The link row stays live at every audience, `Only me` included. The link is not conjured by
 * making the deck public — it is the deck's address, and its owner can copy it, open it and keep it
 * for later whether or not anyone else could follow it yet. Dimming the row would be the menu
 * refusing to hand somebody their own URL, and it would take one more click and a reopened menu to
 * get it. What `Only me` withholds is other people's access, which is the visibility row's business
 * and is already stated one line above.
 */
export function ShareMenu({
  scope,
  onScopeChange,
  url,
  onCopyLink,
  copied = false,
  defaultScopeMenuOpen = false,
  theme: themeProp,
  className,
}: ShareMenuProps) {
  const theme = useV15Theme(themeProp);
  const [open, setOpen] = useState(defaultScopeMenuOpen);
  /**
   * What is still on screen, which during an exit is one menu longer than what is open.
   *
   * The third copy of this shape in v1.5 — `HeaderV15` and `DirectoryPanel` each carry their own,
   * keyed to their own menu union. All three should collapse into one hook; none of them should
   * grow a fourth sibling first.
   */
  const [rendered, setRendered] = useState(defaultScopeMenuOpen);
  const closing = rendered && !open;
  /** The row and the select together: the region a pointer may be inside without dismissing it. */
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }
    if (!rendered) return;
    const timer = setTimeout(() => setRendered(false), SCOPE_MENU_EXIT_MS);
    return () => clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;

    // CAPTURE, and it stops the event dead. `HeaderV15` closes the whole share menu on Escape from
    // its own document listener; without this, one press would close both and the select's own
    // dismissal would never be seen. A capture-phase listener on `document` runs before any
    // bubble-phase one on the same node, so escaping the select cannot escape its parent too.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    // Anything pressed outside the row-plus-select dismisses, INCLUDING the copy row below it.
    // Inside is skipped so that pressing the row itself is left to its own click handler — a
    // pointerdown that closed first would make the click that follows reopen what it just shut.
    const onDown = (e: PointerEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  return (
    <MenuSurface width={SHARE_MENU_WIDTH} theme={theme} label="Share" className={className}>
      <div ref={anchorRef} className={styles.scopeAnchor}>
        {/* No `selected` while the select is open: v1.5's header rule is that active never paints,
            and the row does not need to — the caret turns over, which is the whole cue. */}
        <MenuRow
          icon={SCOPE_ICON[scope]}
          label="Deck is visible for"
          theme={theme}
          trailing={
            <span className={styles.scope}>
              {SCOPE_LABEL[scope]}
              <span className={styles.caret} data-open={open}>
                <DeckIcon name="arrow-down-s" size={14} />
              </span>
            </span>
          }
          onClick={() => setOpen((v) => !v)}
        />

        {rendered ? (
          <div className={styles.scopeLayer} data-closing={closing}>
            <MenuSurface width={SCOPE_MENU_WIDTH} theme={theme} label="Deck is visible for">
              {SCOPE_ORDER.map((option) => (
                <MenuRow
                  key={option}
                  icon={SCOPE_ICON[option]}
                  label={SCOPE_LABEL[option]}
                  theme={theme}
                  trailing={
                    option === scope ? (
                      <span className={styles.tick}>
                        <DeckIcon name="check" size={16} />
                      </span>
                    ) : undefined
                  }
                  onClick={() => {
                    // Picking the live one is a dismissal, not a change: the caller hears about
                    // audiences that actually changed and nothing else.
                    if (option !== scope) onScopeChange?.(option);
                    setOpen(false);
                  }}
                />
              ))}
            </MenuSurface>
          </div>
        ) : null}
      </div>

      <MenuRow
        icon="Copy"
        label={copied ? 'Link copied' : (url ?? 'Copy link')}
        theme={theme}
        shortcut="⌘C"
        onClick={onCopyLink}
      />
    </MenuSurface>
  );
}
